// Spec: docs/specs/webchat-agent-message-display.md §2,§11,§6a
// PV ref: gateway/runai-activity-tracker.ts SUPPRESSED_META_TOOLS (canonical)
//
// Client-side belt-and-suspenders filter for control-plane meta tools the
// runtime emits but must never render as user-visible activity rows.
// The server (createRunAiActivityTracker) is canonical — this is defense in
// depth so a regression there does not leak meta tools into the bubble.

import type { ApiRunAiActivityActionItem } from '../../api/types';

export const SUPPRESSED_META_TOOLS: ReadonlySet<string> = new Set([
  'report_turn_end',
]);

// Pure-noise preview-pill patterns that leak from the gateway as
// "Using ${toolName}" placeholders OR transient "Running command" pills
// that appear for ~1 frame during the B->D state transition (turn-end teardown).
// The v5 AssistantInlineExecRows component owns the live status surface; these
// literal pills are duplicate noise and must be dropped at render time.
//
// D3 fix (v5 polish): RUNNING_PATTERN added to absorb the B->D transition leak
// where "Running command" / "Running ${tool}" leak through StreamingText preview
// for 1 frame before the durable swap clears them.
// PV ref: minicodex codex-rs/tui/src/chatwidget.rs  clears
// in-flight streaming widgets atomically — web equivalent is to filter here.
//
// Patterns matched case-sensitively against the trimmed previewText.
const USING_TOOL_PATTERN = /^Using\s+[A-Za-z0-9_:.-]+$/;
const RUNNING_PATTERN = /^Running(\s+[A-Za-z0-9_:.\- ]+)?$/;

export function isNoisePreviewLabel(previewText: string | undefined): boolean {
  if (!previewText) return false;
  const trimmed = previewText.trim();
  if (!trimmed) return false;
  return USING_TOOL_PATTERN.test(trimmed) || RUNNING_PATTERN.test(trimmed);
}

export function filterSuppressedActions(
  items: ApiRunAiActivityActionItem[] | undefined,
): ApiRunAiActivityActionItem[] {
  if (!items || items.length === 0) return [];
  return items.filter((it) => !SUPPRESSED_META_TOOLS.has(it.toolName));
}
